import type { EventSeverity, Game } from '../../types/game'
import { SeasonIcon } from '../shared/ResourceIcon'

interface TrackerBarProps {
  game: Game
}

const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const

function calculateFavorForEvent(eventIndex: number, steps: Record<number, number>): number {
  let nextEventAt = 0
  for (let n = 0; n <= eventIndex; n += 1) {
    const stepSizeIndex = Math.max(...Object.keys(steps).map(Number).filter((step) => step <= n))
    nextEventAt += steps[stepSizeIndex]
  }
  return nextEventAt
}

export function TrackerBar({ game }: TrackerBarProps) {
  const eventCounter = game.event_handler.event_counter ?? 0
  const steps = game.event_handler.event_trigger_steps
  const severityStep = game.event_handler.events_per_severity_step ?? 2
  const previousEventFavor = eventCounter > 0 ? calculateFavorForEvent(eventCounter - 1, steps) : ''
  const nextEventFavor = calculateFavorForEvent(eventCounter, steps)
  const upcomingEventFavor = calculateFavorForEvent(eventCounter + 1, steps)

  const severityForEvent = (eventIndex: number): EventSeverity =>
    (['base', 'critical', 'catastrophe'] as const)[Math.min(Math.floor(eventIndex / severityStep), 2)]

  const severityClass = (eventIndex: number) => {
    const severity = severityForEvent(eventIndex)
    return severity === 'base' ? '' : ` severity-${severity}`
  }

  return (
    <div className="tracker-container">
      <div className="favor-tracker">
        <div className="favor-tracker-markers">
          {game.players.map((player) => (
            <div
              key={player.color}
              className={`favor-tracker-marker ${player.color}`}
            >
              {player.n_favor}
            </div>
          ))}
        </div>
      </div>

      <div className="season-tracker">
        <div className="season-tracker-seasons">
          {SEASONS.map((season) => (
            <SeasonIcon
              key={season}
              season={season}
              size="5vh"
              inverted={season === game.current_season}
            />
          ))}
        </div>
      </div>

      <div className="queen-tracker">
        <div className="queen-tracker-content">
          <div className="queen-tracker-marker">
            {game.queen.health}
          </div>
        </div>
      </div>

      <div className="colony-collapse-event-tracker">
        <div className="colony-collapse-event-tracker-content">
          <div>{previousEventFavor}</div>
          <div className={severityClass(eventCounter).trim() || undefined}>{nextEventFavor}</div>
          <div className={severityClass(eventCounter + 1).trim() || undefined}>{upcomingEventFavor}</div>
        </div>
      </div>
    </div>
  )
}
